import React, { useEffect, useState } from 'react';
import GeneralForm from '../../components/generalForm';
import CustomSelect from '../../components/customFields/customSelect';
import utils from '../../utils/utils';
import { useDelivery, useDeliveryCreate, useDeliveryEdit, useRoutes } from './apiHooks';
import RichText from '../../components/customFields/richText';
import GooglePlacesField from '../../components/customFields/googlePlacesField';
import { useIntl } from 'react-intl';

function DeliveryForm({ onSuccess, routePlanId, deliveryId, compact = false }) {
  const { formatMessage } = useIntl();

  const [formDelivery, setFormDelivery] = useState({
    routePlanId,
  });

  const {
    isLoading: loading,
    isError,
    error,
  } = useDelivery({
    deliveryId: deliveryId,
    onSuccess: (delivery) => {
      setFormDelivery({
        ...delivery,
        address: {
          street: delivery.address,
          unit: delivery.unit,
          coordinates: delivery.coordinates,
        },
      });
    },
  });

  const { routes } = useRoutes({
    routePlanId: formDelivery?.routePlanId,
  });

  useEffect(() => {
    if (routes?.length) {
      const deliveryRoute = routes.find((r) => r.deliveries.some((d) => d.id === formDelivery?.id));
      if (deliveryRoute) {
        setFormDelivery({
          ...formDelivery,
          routeId: deliveryRoute.id,
        });
      }
    }
  }, [routes, formDelivery.id]);

  const {
    createDelivery,
    isLoading: createLoading,
    isError: isCreateError,
    error: createError,
  } = useDeliveryCreate((delivery) => onSuccess(delivery));

  const {
    editDelivery,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useDeliveryEdit((delivery) => onSuccess(delivery));

  const routeOptions =
    routes?.map((route) => {
      return {
        value: route.id,
        label: formatMessage(
          { id: `delivery.form.field.route.id.value` },
          {
            routeId: route.id,
            driver: route.driver ? route.driver.name : '-',
          }
        ),
      };
    }) || [];

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      name: {
        type: 'string',
        title: formatMessage({ id: 'delivery.form.field.name.label' }),
      },
      phoneNumber: {
        type: 'string',
        title: formatMessage({ id: 'delivery.form.field.phone.number.label' }),
      },
      content: {
        type: 'string',
        title: formatMessage({ id: 'delivery.form.field.content.label' }),
      },
      address: {
        compact: compact,
        type: 'object',
        required: [],
        properties: {
          unit: {
            type: 'string',
            default: '',
          },
          street: {
            type: 'string',
            default: '',
          },
          city: {
            type: 'string',
            default: '',
          },
          country: {
            type: 'string',
            default: '',
          },
          postCode: {
            type: 'string',
            default: '',
          },
          coordinates: {
            type: 'object',
            required: [],
            properties: {
              lat: {
                type: 'string',
              },
              lon: {
                type: 'string',
              },
            },
          },
        },
      },
      instructions: {
        type: 'string',
        title: formatMessage({ id: 'delivery.form.field.instructions.label' }),
      },
    },
  };

  if (routes?.length) {
    formSchema.properties.routeId = {
      type: 'string',
      title: formatMessage({ id: 'delivery.form.field.route.id.label' }),
      options: routeOptions,
      showClearOption: true,
    };
  }

  const uiSchema = {
    address: {
      'ui:field': 'googlePlacesField',
    },
    content: {
      'ui:field': 'richText',
    },
    instructions: {
      'ui:field': 'richText',
    },
    routeId: {
      'ui:field': 'customSelect',
    },
  };

  const onFormSubmit = () => {
    try {
      if (deliveryId) {
        editDelivery({ delivery: formDelivery });
      } else {
        createDelivery({ delivery: formDelivery });
      }
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  const formFields = {
    googlePlacesField: GooglePlacesField,
    customSelect: CustomSelect,
    richText: RichText,
  };

  const buttonCopy = deliveryId ? 'delivery.edit.form.submit' : 'delivery.create.form.submit';

  return (
    <div>
      <GeneralForm
        title={
          deliveryId
            ? formatMessage({ id: 'delivery.form.title.edit' }, { name: formDelivery?.name })
            : formatMessage({ id: 'delivery.form.title.create' })
        }
        onChange={(event) => setFormDelivery(event.formData)}
        onSubmit={onFormSubmit}
        formSchema={formSchema}
        formUiSchema={uiSchema}
        formFields={formFields}
        formData={formDelivery}
        buttonCopy={buttonCopy}
        errorMessage={
          isError || isEditError || isCreateError || error
            ? utils.getErrorMessageFromResponse(createError || editError || createError || error, true)
            : null
        }
        loading={loading || editLoading || createLoading}
        hideImageUpload={true}
        compact={true}
      />
    </div>
  );
}

export default DeliveryForm;
